import { noChange, type ElementPart } from 'lit'
import { AsyncDirective, directive } from 'lit/async-directive.js'
import { PartType } from 'lit/directive.js'
import {
	BehaviorSubject,
	EMPTY,
	Subject,
	combineLatest,
	distinctUntilChanged,
	switchMap,
	takeUntil,
	tap,
	timer,
} from 'rxjs'
import { reducedMotion$ } from './reduced-motion'

export interface LiftOptions {
	value: number | string
	/** Direction of the value change, independent of the pressed side. */
	direction: -1 | 0 | 1
	/** Hold while pressed; false starts the post-release dwell. */
	active: boolean
	/** Advance on every down, repeat and release, including refused presses. */
	trigger: number
	refused?: boolean
}

/** A value above the finger. Supply a relative, overflow-visible host; undefined cancels. */
class LiftDirective extends AsyncDirective {
	private host?: HTMLElement
	private echo?: HTMLSpanElement
	private digits?: Text
	private animation?: Animation
	private exiting = false
	private expired?: LiftOptions
	private readonly options$ = new BehaviorSubject<LiftOptions | undefined>(undefined)
	private readonly teardown$ = new Subject<void>()

	render(_options?: LiftOptions) {
		return noChange
	}

	override update(part: ElementPart, [options]: [LiftOptions?]) {
		if (part.type !== PartType.ELEMENT || !(part.element instanceof HTMLElement)) {
			throw new Error('lift directive must be used on an HTML element')
		}
		if (!this.host) {
			this.host = part.element
			if (this.isConnected) this.listen()
		}
		const previous = this.options$.value
		if (
			previous?.trigger !== options?.trigger ||
			previous?.value !== options?.value ||
			previous?.direction !== options?.direction ||
			previous?.active !== options?.active ||
			previous?.refused !== options?.refused
		) {
			this.options$.next(options ? { ...options } : undefined)
		}
		return noChange
	}

	override reconnected() {
		this.listen()
	}

	override disconnected() {
		this.teardown$.next()
		this.animation?.cancel()
		this.animation = undefined
		this.echo?.remove()
		this.echo = undefined
		this.digits = undefined
		this.options$.next(undefined)
		this.expired = undefined
		this.exiting = false
	}

	private listen() {
		this.teardown$.next()
		combineLatest([this.options$, reducedMotion$.pipe(distinctUntilChanged())])
			.pipe(
				switchMap(([options, reduced]) => {
					if (!options || !this.host) {
						this.animation?.cancel()
						this.animation = undefined
						if (this.echo) this.echo.hidden = true
						this.exiting = false
						return EMPTY
					}
					// A preference change must not resurrect an already expired answer.
					if (options === this.expired) return EMPTY
					if (!this.echo) {
						this.echo = document.createElement('span')
						this.echo.setAttribute('data-schmancy-lift', '')
						this.echo.setAttribute('aria-hidden', 'true')
						this.echo.style.cssText =
							'position:absolute;bottom:calc(100% + 28px);right:12px;font-size:1.5rem;font-weight:700;font-variant-numeric:tabular-nums;line-height:1;pointer-events:none;user-select:none;z-index:20;background:var(--schmancy-sys-color-surface-containerHigh);border:1px solid var(--schmancy-sys-color-outlineVariant);border-radius:var(--schmancy-radius-xs, 0.4375rem);padding:4px 8px;min-width:48px;box-sizing:border-box;text-align:center;opacity:1;transform:translateY(0);'
						this.digits = document.createTextNode('')
						this.echo.appendChild(this.digits)
						this.echo.hidden = true
						this.host.appendChild(this.echo)
					}
					if (this.digits) this.digits.data = String(options.value)
					this.echo.style.color =
						options.direction > 0 && !options.refused
							? 'var(--schmancy-sys-color-primary-default)'
							: 'var(--schmancy-sys-color-surface-on)'
					this.echo.style.textDecoration = options.refused ? 'line-through' : 'none'
					if (reduced || this.exiting) {
						this.animation?.cancel()
						this.animation = undefined
						this.exiting = false
					}
					if (this.echo.hidden) {
						this.echo.hidden = false
						if (!reduced) {
							this.animation = this.echo.animate(
								[
									{ opacity: 0, transform: 'translateY(10px)' },
									{ opacity: 1, transform: 'translateY(0)' },
								],
								{ duration: 160, easing: 'ease-out' },
							)
						}
					}
					if (options.active) return EMPTY
					return timer(reduced ? 1100 : 420).pipe(
						switchMap(() => {
							if (!reduced && this.echo) {
								this.animation?.cancel()
								this.exiting = true
								this.animation = this.echo.animate([{ opacity: 1 }, { opacity: 0 }], {
									duration: 200,
									easing: 'ease-in',
									fill: 'forwards',
								})
							}
							return timer(reduced ? 0 : 200)
						}),
						tap(() => {
							if (this.echo) this.echo.hidden = true
							this.animation?.cancel()
							this.animation = undefined
							this.exiting = false
							this.expired = options
						}),
					)
				}),
				takeUntil(this.teardown$),
			)
			.subscribe()
	}
}

export const lift = directive(LiftDirective)
